import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Wand2 } from 'lucide-react';

const SearchingIndicator = ({ mode = 'search' }) => {
  const isCoachMode = mode === 'coach';
  
  // Mode-specific content
  const modeContent = {
    search: {
      title: "Searching the Open Org Codex",
      subtitle: "Scanning consulting wisdom and curated knowledge...",
      stages: ['Analyzing', 'Searching', 'Assembling'],
      icon: Wand2,
      iconColor: 'text-indigo-500',
      gradientFrom: 'from-indigo-500/30',
      gradientTo: 'to-purple-500/30',
      borderColor: 'border-indigo-500/40',
      ringColor: 'border-indigo-500/30',
      dotColor: 'bg-indigo-500/60'
    },
    coach: {
      title: "Processing your challenge",
      subtitle: "Thinking through possibilities and approaches...",
      stages: ['Listening', 'Analyzing', 'Advising'],
      icon: Brain,
      iconColor: 'text-green-500',
      gradientFrom: 'from-green-500/30',
      gradientTo: 'to-emerald-500/30',
      borderColor: 'border-green-500/40',
      ringColor: 'border-green-500/30',
      dotColor: 'bg-green-500/60'
    }
  };

  const content = modeContent[mode];
  const IconComponent = content.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800/30 backdrop-blur-xl rounded-2xl border border-gray-700/30 p-6 shadow-xl"
    >
      <div className="flex items-center space-x-4">
        {/* Magical Processing Orb */}
        <div className="relative flex-shrink-0">
          {/* Central Orb */}
          <motion.div
            animate={{ 
              scale: [1, 1.05, 1],
              opacity: [0.8, 1, 0.8]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
            className={`w-12 h-12 rounded-full bg-gradient-to-br ${content.gradientFrom} ${content.gradientTo} border ${content.borderColor} flex items-center justify-center relative overflow-hidden`}
          >
            {/* Inner glow */}
            <motion.div
              animate={{ 
                rotate: 360 
              }}
              transition={{ 
                duration: 8, 
                repeat: Infinity, 
                ease: "linear" 
              }}
              className={`absolute inset-2 rounded-full bg-gradient-to-r ${content.gradientFrom.replace('/30', '/20')} via-transparent ${content.gradientTo.replace('/30', '/20')}`}
            />
            
            {/* Mode-specific icon in center */}
            <IconComponent className={`w-5 h-5 ${content.iconColor} relative z-10`} />
          </motion.div>

          {/* Scanning Rings */}
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ 
                scale: [0.8, 1.8, 2.2],
                opacity: [0, 0.6, 0]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeOut"
              }}
              className={`absolute inset-0 rounded-full border ${content.ringColor}`}
            />
          ))}

          {/* Floating Knowledge/Thought Dots */}
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: [0, 1, 0],
                x: [0, Math.cos(i * 1.57) * 25],
                y: [0, Math.sin(i * 1.57) * 25],
                scale: [0, 1, 0]
              }}
              transition={{ 
                duration: 2.5,
                repeat: Infinity,
                delay: i * 0.4,
                ease: "easeOut"
              }}
              className="absolute top-1/2 left-1/2 w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2"
            >
              <div className={`w-full h-full rounded-full ${content.dotColor}`} />
            </motion.div>
          ))}
        </div>

        {/* Status Text */}
        <div className="flex-1 min-w-0">
          <motion.h3 
            className="text-white font-medium text-sm mb-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {content.title}
          </motion.h3>
          
          <motion.p 
            className="text-gray-400 text-xs leading-relaxed"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            {content.subtitle}
          </motion.p>

          {/* Progress Indicators */}
          <div className="flex items-center space-x-2 mt-3">
            {content.stages.map((stage, index) => (
              <motion.div
                key={stage}
                initial={{ opacity: 0.3 }}
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: index * 0.5,
                  ease: "easeInOut"
                }}
                className="flex items-center space-x-1"
              >
                <div className={`w-1 h-1 rounded-full ${content.dotColor}`} />
                <span className="text-xs text-gray-500">{stage}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SearchingIndicator; 